import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { shortDateTime } from '../../shared/dateTime';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { useStillMounted } from '../../shared/recordScoped';
import {
  MIN_EXCEPTION_REASON_LENGTH,
  PUBLICATION_EXCEPTION_APPROVE_LABEL,
  approveException,
  exceptionStateSentence,
  fetchOrderExceptions,
  notifyPublicationExceptionsChanged,
  rejectException,
  requestException,
  withdrawException,
  type ExceptionQueueItem,
} from './publicationExceptionsApi';
import type { PostingGates } from './publicationsApi';
import { ContractTestId } from '../../usage/ContractTestId';

type Decision = { kind: 'request' } | { kind: 'reject'; id: number } | { kind: 'approve'; id: number };

/** Ile ostatnich wniosków zlecenia pokazujemy pod bieżącym. */
const HISTORY_SHOWN = 3;

/**
 * Wniosek o wyjątek od bramki publikacji przy zleceniu (makieta back office
 * v2, fala B, 21.10.2026: BR-SAL-01, BR-TRD-04; ścieżki TRD-03 kroki 3–4,
 * MIX-03 kroki 3–4; przełącznik `publicationExceptionRequests`).
 *
 * - Trader, który nie może udzielić wyjątku ani potwierdzić warunków,
 *   „Prosi o wyjątek” z powodem (co najmniej 10 znaków). Wniosek trafia do
 *   osób z uprawnieniem „Akceptacja wyjątku od zakazu podwykonawcy” (lista,
 *   licznik w menu, dzwonek).
 * - Stan wniosku widać tu każdemu, kto widzi sekcję publikacji — także
 *   planiście (FTL widzi, że Trade czeka na zgodę).
 * - Osoba uprawniona zatwierdza albo odrzuca wniosek także stąd (z dzwonka
 *   trafia na kartę zlecenia); własnego wniosku nie rozstrzyga.
 * - Zatwierdzony wniosek przepuszcza publikację przez bramkę — okno
 *   publikacji to pokazuje i nie pyta o potwierdzenie.
 */
export const PublicationExceptionSection = ({
  userOfferId,
  eligibility,
  canTrade,
  onChanged,
}: {
  userOfferId: number | string;
  eligibility: PostingGates['dopuszczalnosc'] | null;
  canTrade: boolean;
  onChanged: () => void;
}) => {
  const notify = useNotify();
  const stillMounted = useStillMounted();
  const [requests, setRequests] = useState<ExceptionQueueItem[] | null>(null);
  const [dialog, setDialog] = useState<Decision | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const lastLoad = useRef(0);

  // Wnioski czytamy tylko wtedy, gdy reguła klienta w ogóle zatrzymuje
  // publikację — przy „dozwolony” wniosków nie ma i nie było po co ich składać.
  const gateNeedsDecision = eligibility?.requiresException === true || eligibility?.requiresConfirmation === true;

  const load = useCallback(async () => {
    if (!gateNeedsDecision) {
      setRequests([]);
      return;
    }
    const call = ++lastLoad.current;
    try {
      const r = await fetchOrderExceptions(userOfferId);
      if (stillMounted() && call === lastLoad.current) setRequests(Array.isArray(r?.requests) ? r.requests : []);
    } catch {
      if (stillMounted() && call === lastLoad.current) setRequests([]);
    }
  }, [userOfferId, stillMounted, gateNeedsDecision]);

  useEffect(() => {
    void load();
  }, [load, eligibility?.exceptionRequest?.id, eligibility?.exceptionRequest?.status]);

  const current = requests?.find((r) => r.status === 'pending' || r.status === 'approved') ?? null;
  const earlier = (requests ?? []).filter((r) => r !== current).slice(0, HISTORY_SHOWN);
  const mayRequest = canTrade && eligibility?.mayRequestException === true;
  if (!current && earlier.length === 0 && !mayRequest) return null;

  const close = () => {
    setDialog(null);
    setReason('');
  };

  const submit = async () => {
    if (!dialog) return;
    setBusy(true);
    try {
      if (dialog.kind === 'request') {
        await requestException(userOfferId, reason.trim());
        notify('Wniosek o wyjątek złożony — czeka na decyzję osoby uprawnionej.', { type: 'success' });
      } else if (dialog.kind === 'approve') {
        await approveException(dialog.id, reason.trim());
        notify('Wyjątek zatwierdzony — trader może zlecić publikację.', { type: 'success' });
      } else {
        await rejectException(dialog.id, reason.trim());
        notify('Wniosek o wyjątek odrzucony.', { type: 'success' });
      }
      close();
      notifyPublicationExceptionsChanged();
      await load();
      onChanged();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (id: number) => {
    setBusy(true);
    try {
      await withdrawException(id);
      notify('Wniosek o wyjątek wycofany.', { type: 'success' });
      notifyPublicationExceptionsChanged();
      await load();
      onChanged();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const reasonRequired = dialog?.kind !== 'approve';
  const reasonTooShort = reasonRequired && reason.trim().length < MIN_EXCEPTION_REASON_LENGTH;
  const title =
    dialog?.kind === 'request'
      ? 'Wniosek o wyjątek od bramki publikacji'
      : dialog?.kind === 'approve'
        ? `Zatwierdzić wniosek nr ${dialog.id}?`
        : `Odrzucić wniosek nr ${dialog?.kind === 'reject' ? dialog.id : ''}?`;

  return (
    <Box data-testid="exception-section" sx={{ mb: 1 }}>
      {current && (
        <Alert
          severity={current.status === 'approved' ? 'success' : 'info'}
          sx={{ mb: 0.5 }}
          data-testid="exception-state"
          action={
            <Stack direction="row" spacing={0.5}>
              {current.canDecide && (
                <>
                  {/* Kontrakt `data-testid` paczki (S-22.10-02): zgoda na wyjątek (u nas osoba z uprawnieniem). */}
                  <ContractTestId id="sal-akceptacje-zgoda" disabled={busy}>
                    <Button
                      size="small"
                      data-testid={`exception-approve-${current.id}`}
                      disabled={busy}
                      onClick={() => setDialog({ kind: 'approve', id: current.id })}
                    >
                      Zatwierdź
                    </Button>
                  </ContractTestId>
                  <Button
                    size="small"
                    color="error"
                    data-testid={`exception-reject-${current.id}`}
                    disabled={busy}
                    onClick={() => setDialog({ kind: 'reject', id: current.id })}
                  >
                    Odrzuć
                  </Button>
                </>
              )}
              {current.mine && current.status === 'pending' && (
                <Button
                  size="small"
                  data-testid={`exception-withdraw-${current.id}`}
                  disabled={busy}
                  onClick={() => void withdraw(current.id)}
                >
                  Wycofaj
                </Button>
              )}
            </Stack>
          }
        >
          {exceptionStateSentence(current, shortDateTime)}
          <Typography variant="caption" component="div" color="text.secondary">
            Powód wniosku: {current.reason}
          </Typography>
        </Alert>
      )}
      {mayRequest && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
          {/* Kontrakt `data-testid` paczki (S-22.10-02): wniosek o wyjątek (u nas do osoby z uprawnieniem). */}
          <ContractTestId id="trd-zlecenie-wniosek-sales" disabled={busy}>
            <Button
              size="small"
              variant="outlined"
              data-testid="exception-request-button"
              disabled={busy}
              onClick={() => setDialog({ kind: 'request' })}
            >
              Poproś o wyjątek
            </Button>
          </ContractTestId>
          <Typography variant="caption" color="text.secondary">
            {eligibility?.requiresException
              ? 'Klient zakazuje podwykonawców — publikacja tylko z wyjątkiem zatwierdzonym przez osobę uprawnioną.'
              : 'Nie możesz potwierdzić warunków klienta? Poproś o wyjątek — zatwierdza go osoba uprawniona.'}
          </Typography>
        </Stack>
      )}
      {earlier.length > 0 && (
        <Stack spacing={0.25} data-testid="exception-history">
          {earlier.map((r) => (
            <Typography key={r.id} variant="caption" color="text.secondary">
              {exceptionStateSentence(r, shortDateTime)}
            </Typography>
          ))}
        </Stack>
      )}

      <Dialog open={dialog !== null} onClose={() => !busy && close()} fullWidth maxWidth="sm">
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {dialog?.kind === 'request' && (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Wniosek trafi do osób z uprawnieniem „{PUBLICATION_EXCEPTION_APPROVE_LABEL}”. Dopiero zatwierdzony
              wniosek pozwoli zlecić publikację; wszystko zapisze się w historii zlecenia. Nic nie wychodzi na
              giełdę ani pocztą.
            </Typography>
          )}
          {dialog?.kind === 'approve' && (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Zatwierdzasz wyjątek od reguły klienta dla tego zlecenia — trader zleci publikację sam. Zatwierdzenie
              zapisze się w historii zlecenia i dzienniku działań.
            </Typography>
          )}
          <TextField
            label={
              dialog?.kind === 'approve'
                ? 'Uwagi (opcjonalnie)'
                : `Powód (co najmniej ${MIN_EXCEPTION_REASON_LENGTH} znaków)`
            }
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            slotProps={{ htmlInput: { 'data-testid': 'exception-reason-input' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            data-testid="exception-submit"
            disabled={busy || reasonTooShort}
            color={dialog?.kind === 'reject' ? 'error' : 'primary'}
            onClick={() => void submit()}
          >
            {dialog?.kind === 'request' ? 'Złóż wniosek' : dialog?.kind === 'approve' ? 'Zatwierdź' : 'Odrzuć'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
